/**
 * BusinessLevantaDetail — Life › Business › Levanta
 * Renders the shared RoadmapBoard against NEXA-scoped endpoints.
 * Both this and the public /roadmap read/write the same MongoDB collection,
 * so edits made here appear on /roadmap and vice versa.
 */
import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Link2 } from 'lucide-react';
import RoadmapBoard from '../pages/roadmap/RoadmapBoard';
import { API_ROOT, getKey } from './api';

const makeApi = () => async (path, opts = {}) => {
  const res = await fetch(`${API_ROOT}/api/nexa/business/roadmap${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'x-nexa-key': getKey(),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

export const BusinessLevantaDetail = ({ onBack }) => {
  const api = useCallback(makeApi(), []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="flex flex-col gap-4"
      data-testid="nexa-business-levanta-view"
    >
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          data-testid="nexa-business-levanta-back"
          className="flex items-center gap-1.5 text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 hover:text-sky-400 active:scale-95 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Business
        </button>
        <a
          href="/roadmap"
          target="_blank"
          rel="noreferrer"
          data-testid="nexa-business-levanta-open-external"
          className="flex items-center gap-1.5 text-[10px] font-mono tracking-[0.2em] uppercase text-white/40 hover:text-sky-400 transition-colors"
        >
          <Link2 className="w-3.5 h-3.5" /> /roadmap
        </a>
      </div>

      {/* The roadmap board keeps its own light aesthetic — anchored in a
          card so it reads as a distinct surface inside NEXA's dark UI. */}
      <div className="rounded-3xl bg-gradient-to-b from-[#eef1fb] via-white to-[#f4f6fc] ring-1 ring-white/10 shadow-2xl px-5 py-8 sm:px-8 sm:py-10 overflow-hidden">
        <RoadmapBoard
          api={api}
          isAdmin
          heroEyebrow="Business · Levanta"
          heroTitle="The Levanta roadmap"
          heroBlurb="Where Levanta is headed. Edits here sync live with /roadmap."
        />
      </div>
    </motion.div>
  );
};

export default BusinessLevantaDetail;
